import * as React from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

import { ALERT_SYSTEM_TYPE_CONFIG } from '../../config/alertSystem.constants'
import type { AlertIconsRowProps, AlertSystemItem } from '../../config/alertSystem.types'
import { useDialog } from '../../../dialog/dialog.hooks'
import ModalAlerts from '../../../modal-alerts/modal-alerts.component'
import { alertIconsRowStyles } from './AlertIconsRow.styles'
import { AlertHoverCard } from '../AlertHover/AlertHoverCard'
import PackagesHoverContent from '../AlertHover/bodies/PackagesHoverContent'
import { alertModalTabOverrides } from './alertModalTabs.constants'
import { resolveHoverData } from '../AlertHover/AlertHoverPopover'
import { TABS_VALUES } from '../../../modal-alerts/notes-properties/modal-alerts.constants'

const TABS_DEFAULT = TABS_VALUES[0]

export const AlertIconsRow: React.FC<AlertIconsRowProps> = ({
  items,
  size = 'md',
  disableDrawer = false,
  maxAlertsToShow,
  clientContext,
}) => {
  const { pushDialog, popDialog } = useDialog()
  const { t } = useTranslation('alerts')

  const alertProps = React.useMemo(() => {
    const props: Record<string, unknown> = {}
    if (!items?.length) {
      return props
    }

    for (const item of items) {
      switch (item.type) {
        case 'payment':
          if (item.data?.failed) {
            props.failedPayments = Array.from({ length: item.data.failed }, (_, i) => ({
              id: `failed-${i}`,
            }))
          }
          if (item.data?.outstanding) {
            props.outstandingPayments = Array.from({ length: item.data.outstanding }, (_, i) => ({
              id: `outstanding-${i}`,
            }))
          }
          break
        case 'birthday':
          props.birthday = item.data?.dateOfBirth || true
          break
        case 'firstSession':
          props.isFirstSession = true
          break
        case 'suspended':
          props.suspendedClient = new Date().toISOString()
          break
        case 'location':
          props.homeLocations = item.data?.homeSiteName ? [item.data.homeSiteName] : []
          break
        case 'introOffers':
          props.introOffer = 'intro'
          break
        case 'forms':
          if (item.data?.count) {
            props.formsPending = Array.from({ length: item.data.count }, (_, i) =>
              t('hover.formsPending', { count: i + 1 })
            )
          }
          break
        case 'agreements':
          break
        case 'packages':
          if (item.data?.count) {
            props.packagesCount = item.data.count
          }
          break
        case 'notesProps':
        case 'daysAbsent':
        case 'destinationFee':
        case 'customAlert':
          break
        default: {
          const exhaustiveCheck: never = item.type
          void exhaustiveCheck
        }
      }
    }
    return props
  }, [items])

  const openAlertDialog = React.useCallback(
    (alert: AlertSystemItem) => {
      if (disableDrawer) {
        return
      }

      const tabValue = alertModalTabOverrides[alert.type] ?? TABS_DEFAULT
      pushDialog(
        ModalAlerts,
        {
          clientId: clientContext?.id ?? alert.clientId,
          name: clientContext?.name ?? '',
          firstName: clientContext?.firstName ?? '',
          profileImg: clientContext?.profileImageUrl
            ? { url: clientContext.profileImageUrl }
            : undefined,
          tabDefaultValue: tabValue,
          closeAction: popDialog,
          hasNotesAndProperties: Boolean(clientContext?.id ?? alert.clientId),
          ...alertProps,
        },
        { closeDialog: popDialog }
      )
    },
    [disableDrawer, pushDialog, popDialog, clientContext, alertProps]
  )

  const handleClick = React.useCallback(
    (event: React.MouseEvent, alert: AlertSystemItem) => {
      event.stopPropagation()
      openAlertDialog(alert)
    },
    [openAlertDialog]
  )

  const sorted = React.useMemo(() => {
    if (!items?.length) {
      return []
    }

    const validItems = items.filter((item) => {
      const config = ALERT_SYSTEM_TYPE_CONFIG[item.type]
      if (!config) {
        console.warn(`Alert type config missing for type: ${item.type}`)
        return false
      }
      return true
    })

    const sortedItems = validItems.sort(
      (a, b) => ALERT_SYSTEM_TYPE_CONFIG[a.type].order - ALERT_SYSTEM_TYPE_CONFIG[b.type].order
    )

    return maxAlertsToShow ? sortedItems.slice(0, maxAlertsToShow) : sortedItems
  }, [items, maxAlertsToShow])

  if (!sorted.length) {
    return null
  }

  return (
    <Box sx={alertIconsRowStyles.root()}>
      {sorted.map((alert, index) => {
        const { Icon, colors: configColors } = ALERT_SYSTEM_TYPE_CONFIG[alert.type]
        const colors = alert.colorsOverride ?? configColors
        const alertKey = `${alert.clientId}_${alert.type}`
        const requiresDynamicData = Boolean(alert.requiresFetch)
        const hoverData = requiresDynamicData ? null : resolveHoverData(alertKey, alert.type, alert)
        const clientIdForFetch = clientContext?.id ?? alert.clientId

        const onOpenAlerts = disableDrawer ? undefined : () => openAlertDialog(alert)

        let tooltipTitle: React.ReactNode = null
        if (requiresDynamicData && alert.type === 'packages' && clientIdForFetch) {
          tooltipTitle = (
            <PackagesHoverContent clientId={clientIdForFetch} onOpenAlerts={onOpenAlerts} />
          )
        } else if (requiresDynamicData) {
          tooltipTitle = (
            <Typography variant="header6" color="common.black">
              {t('dynamicData.comingSoon')}
            </Typography>
          )
        } else if (hoverData) {
          tooltipTitle = <AlertHoverCard data={hoverData} onOpenAlerts={onOpenAlerts} />
        }
        const containerSx = alertIconsRowStyles.container(index)
        const chipSx = alertIconsRowStyles.chip(colors, index, size)

        if (disableDrawer) {
          containerSx.cursor = 'default'
          chipSx.cursor = 'default'
        }

        return (
          <Tooltip
            key={alertKey}
            title={tooltipTitle}
            placement="bottom"
            enterDelay={175}
            leaveDelay={250}
            slotProps={{
              tooltip: {
                sx: alertIconsRowStyles.tooltip,
              },
            }}
          >
            <Box sx={containerSx} onClick={(e) => e.stopPropagation()}>
              <Box sx={chipSx} onClick={(e) => handleClick(e, alert)}>
                <Icon />
              </Box>
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}

export default AlertIconsRow
